import React from 'react';
import { Typography, Card, Space, Button, Divider, Alert } from 'antd';
import { ArrowLeft, FileText, Users, AlertTriangle, Scale, Crown } from 'lucide-react';
import PageContainer from './PageContainer';

const { Title, Text, Paragraph } = Typography;

interface TermsOfServiceProps {
  onBack?: () => void;
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack }) => {
  return (
    <PageContainer>
      {/* 头部 */}
      <div style={{ marginBottom: 32 }}>
        {onBack && (
          <Button
            icon={<ArrowLeft size={16} />}
            onClick={onBack}
            style={{ marginBottom: 16 }}
          >
            返回
          </Button>
        )}
        <Title level={2}>
          <FileText size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          服务条款
        </Title>
        <Text type="secondary">
          最后更新时间：2025年7月12日
        </Text>
      </div>

      {/* 重要提示 */}
      <Alert
        message="重要提示"
        description="请仔细阅读本服务条款。使用小酷笔记服务即表示您同意遵守本条款的所有规定。"
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* 服务概述 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>1. 服务概述</Title>

        <Paragraph>
          小酷笔记是一款智能笔记应用，为用户提供以下服务：
        </Paragraph>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>• 笔记创建、编辑、存储和同步</Text>
          <Text>• Markdown 格式支持</Text>
          <Text>• 标签管理和分类功能</Text>
          <Text>• AI 智能助手服务</Text>
          <Text>• 云端数据同步</Text>
          <Text>• 多平台访问（网页版、桌面版）</Text>
        </Space>

        <Paragraph style={{ marginTop: 16 }}>
          我们保留随时修改、暂停或终止部分或全部服务的权利，
          并会提前通知用户重大变更。
        </Paragraph>
      </Card>

      {/* 用户账户 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>
          <Users size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          2. 用户账户
        </Title>

        <Title level={4}>2.1 账户注册</Title>
        <Paragraph>
          • 您必须提供真实、准确的注册信息
          <br />
          • 每个邮箱地址只能注册一个账户
          <br />
          • 您有责任保护账户密码的安全
          <br />
          • 禁止创建多个账户恶意使用服务
        </Paragraph>

        <Title level={4}>2.2 账户安全</Title>
        <Paragraph>
          • 请定期更新密码，使用强密码
          <br />
          • 不要与他人共享账户信息
          <br />
          • 发现账户异常请立即联系我们
          <br />
          • 您对账户下的所有活动承担责任
        </Paragraph>
      </Card>

      {/* 使用规范 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>
          <AlertTriangle size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          3. 使用规范
        </Title>

        <Title level={4}>3.1 禁止行为</Title>
        <Paragraph>
          使用我们的服务时，您不得：
        </Paragraph>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>• 上传违法、有害、威胁、辱骂或诽谤性内容</Text>
          <Text>• 侵犯他人知识产权或隐私权</Text>
          <Text>• 传播病毒、恶意代码或有害程序</Text>
          <Text>• 尝试未经授权访问我们的系统</Text>
          <Text>• 干扰或破坏服务的正常运行</Text>
          <Text>• 使用自动化工具大量访问服务</Text>
          <Text>• 将服务用于商业目的（除非获得授权）</Text>
        </Space>

        <Title level={4}>3.2 内容责任</Title>
        <Paragraph>
          • 您对上传的所有内容承担完全责任
          <br />
          • 我们有权删除违规内容但无义务审查所有内容
          <br />
          • 严重违规可能导致账户被暂停或终止
        </Paragraph>
      </Card>

      {/* VIP 服务 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>
          <Crown size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          4. VIP 服务
        </Title>

        <Title level={4}>4.1 VIP 特权</Title>
        <Paragraph>
          VIP 用户享有以下特权：
        </Paragraph>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>• 无限次 AI 对话</Text>
          <Text>• 无限笔记存储空间</Text>
          <Text>• 优先技术支持</Text>
          <Text>• 高级功能访问权限</Text>
          <Text>• 数据导出功能</Text>
        </Space>

        <Title level={4}>4.2 付费条款</Title>
        <Paragraph>
          • VIP 服务采用预付费模式
          <br />
          • 费用一经支付不予退还（法律规定除外）
          <br />
          • 服务到期后自动降级为免费用户
          <br />
          • 价格可能调整，现有用户不受影响
        </Paragraph>
      </Card>

      {/* 知识产权 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>
          <Scale size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          5. 知识产权
        </Title>

        <Title level={4}>5.1 我们的权利</Title>
        <Paragraph>
          • 小酷笔记的所有技术、设计、商标归我们所有
          <br />
          • 未经许可不得复制、修改或分发我们的软件
          <br />
          • 我们保留所有未明确授予的权利
        </Paragraph>

        <Title level={4}>5.2 用户内容</Title>
        <Paragraph>
          • 您保留对自己创建内容的所有权
          <br />
          • 您授权我们存储、处理和传输您的内容以提供服务
          <br />
          • 我们不会将您的内容用于服务提供以外的目的
        </Paragraph>
      </Card>

      {/* 免责声明 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>6. 免责声明</Title>

        <Paragraph>
          • 服务按"现状"提供，我们不保证服务无中断或无错误
          <br />
          • 我们不对因使用服务造成的任何损失承担责任
          <br />
          • 用户应自行备份重要数据
          <br />
          • 我们的责任限制在适用法律允许的最大范围内
        </Paragraph>

        <Alert
          message="数据备份提醒"
          description="虽然我们采用多重备份保护您的数据，但仍建议您定期导出重要笔记作为备份。"
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Card>

      {/* 服务变更 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>7. 服务变更和终止</Title>

        <Title level={4}>7.1 服务变更</Title>
        <Paragraph>
          • 我们可能随时修改或更新服务功能
          <br />
          • 重大变更会提前 30 天通知用户
          <br />
          • 继续使用服务表示接受变更
        </Paragraph>

        <Title level={4}>7.2 服务终止</Title>
        <Paragraph>
          • 您可以随时停止使用服务并删除账户
          <br />
          • 我们可能因违规行为终止您的账户
          <br />
          • 服务终止前会提供数据导出机会
        </Paragraph>
      </Card>

      {/* 争议解决 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={3}>8. 争议解决</Title>

        <Paragraph>
          • 本条款受中华人民共和国法律管辖
          <br />
          • 争议优先通过友好协商解决
          <br />
          • 协商不成的，提交北京市朝阳区人民法院管辖
        </Paragraph>
      </Card>

      {/* 联系信息 */}
      <Card>
        <Title level={3}>9. 联系我们</Title>

        <Paragraph>
          如果您对本服务条款有任何疑问，请联系我们：
        </Paragraph>

        <Space direction="vertical" size="small">
          <Text>• 邮箱：xiaoku.name666.top</Text>
          <Text>• 客服：daily.name666.top</Text>
          <Text>• 地址：中国唐山市曹妃甸区渤海大道21号</Text>
          <Text>• 电话：15633971706</Text>
        </Space>

        <Divider />

        <Text type="secondary">
          本服务条款自发布之日起生效，小酷笔记保留最终解释权。
        </Text>
      </Card>
    </PageContainer>
  );
};

export default TermsOfService;
